<template>
  <div class="video-item">
    <video class="video-video" :src="videoUrl" ref="videoRef" autoplay :controls="false"
      controlslist="nodownload nofullscreen noremoteplayback" x5-video-player-type="h5" webkit-playsinline="true"
      x-webkit-airplay="true" playsinline="true" x5-playsinline preload="metadata" disablepictureinpicture
      disableremoteplayback @click="togglePlay" @ended="showPauseBtn = true"></video>
    <div class="video-cover">
      <div class="play-icon">
        <img v-show="showPauseBtn" class="play-icon-inner"
          src="https://p4-ky.wsukwai.com/udata/pkg/KMOVIE-CDN/image/playIcon3.png" />
      </div>

      <div class="progress">
        <div class="timer" :style="{ opacity: progressOpacity }">
          <div class="time-box">{{ progressFormat }} / <span class="duration">{{ durationFormat }}</span></div>
        </div>
        <van-slider v-model="progress" bar-height="1px" :step="0.01" :min="0" :max="duration" @change="handleDrag"
          @drag-start="handleDragStart" @drag-end="handleDragEnd">
          <template #button>
            <div class="custom-button" :style="{ opacity: progressOpacity }"></div>
          </template>
        </van-slider>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { VideoHTMLAttributes } from 'vue';
import { Slider as VanSlider } from 'vant';

const durationToSecond = (duration: number) => {
  const totalSeconds = Math.ceil(duration / 1000); // 少于1s向上取整
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // 在小时、分钟和秒钟上添加前导零（如果需要）
  const hoursString = hours ? hours.toString().padStart(2, '0') : '';
  const minutesString = minutes.toString().padStart(2, '0');
  const secondsString = seconds.toString().padStart(2, '0');

  return [hoursString, minutesString, secondsString].filter(Boolean).join(':');
};

const props = defineProps<{ src: VideoHTMLAttributes['src'] }>()

const canUse = ref(true);
const showPauseBtn = ref(false);
const videoRef = ref<HTMLVideoElement>();
const progress = ref(0);
const duration = ref(Number.MAX_SAFE_INTEGER);

const progressFormat = computed(() => durationToSecond(progress.value * 1000 || 0));
const durationFormat = computed(() => durationToSecond(duration.value * 1000 || 0));

defineExpose({ videoRef })

const videoUrl = computed(() => {
  if (!videoRef.value) return '';
  if (!canUse.value) {
    console.log(videoRef.value, `实例视频现已置空，原视频url:${props.src}`)
    return '';
  }
  console.log(videoRef.value, `实例视频url置为：${props.src}`);
  return props.src;
});

const lastPlay = ref(true);
const visibleChange = () => {
  if (document?.visibilityState === 'visible') {
    if (lastPlay.value) videoRef.value?.play();
  }
  if (document?.visibilityState === 'hidden') {
    lastPlay.value = !videoRef.value?.paused ?? lastPlay.value;
    videoRef.value?.pause();
  }
}
const isPlaying = () => {
  const video = videoRef?.value;
  if (!video) return false
  return !(video.paused || video.ended || video.seeking)
}

const timer = ref<ReturnType<typeof setTimeout>>();
const progressOpacity = ref(0);

const activeProgress = () => {
  progressOpacity.value = 1;
  if (timer.value) {
    clearTimeout(timer.value);
  }
  timer.value = setTimeout(() => { progressOpacity.value = 0; }, 2000)
}

const handleDragStart = () => {
  clearTimeout(timer.value);
  progressOpacity.value = 1;
  if (lastPlay.value) {
    showPauseBtn.value = false;
    videoRef.value?.pause(); // 拖进度条时暂停播放
  }
}

const handleDragEnd = () => {
  if (lastPlay.value) {
    showPauseBtn.value = false;
    videoRef.value?.play();// 拖拽结束继续播放
  }
  activeProgress();
}

const togglePlay = () => {
  const video = videoRef?.value;
  activeProgress();
  if (isPlaying()) {
    video?.pause();
    showPauseBtn.value = true;
  } else {
    video?.play();
    showPauseBtn.value = false;
  }
  lastPlay.value = !showPauseBtn.value;
};

const handleDrag = (value: number) => {
  if (!videoRef.value) return;
  activeProgress();
  if (videoRef.value.paused) {
    showPauseBtn.value = true;
  }
  videoRef.value.currentTime = progress.value || 0;
}

const handleTimeUpdate = () => {
  progress.value = videoRef.value?.currentTime || 0;
  duration.value = videoRef.value?.duration || 0;
}

window.addEventListener('beforeunload', () => {
  canUse.value = false;
});

onMounted(() => {
  canUse.value = true;
  document?.addEventListener('visibilitychange', visibleChange);
  videoRef.value?.addEventListener('timeupdate', handleTimeUpdate);
});
onBeforeUnmount(() => {
  canUse.value = false;
  document?.removeEventListener('visibilitychange', visibleChange);
  videoRef.value?.removeEventListener('timeupdate', handleTimeUpdate);
  clearTimeout(timer.value);
})

</script>
<style scoped lang="less">
.video-item {
  display: flex;
  align-items: center;
  justify-content: center;

  .video-video {
    width: 100%;
    height: auto;
  }

  .video-cover {
    position: absolute;
    pointer-events: none;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
  }

  .play-icon {
    position: absolute;
    left: 50%;
    top: 50%;
    margin-top: -0.16rem;
    margin-left: -0.36rem;
    width: 0.8rem;
    height: 0.8rem;
    font-size: 0;
    z-index: 3;
  }

  .play-icon-inner {
    width: 100%;
    height: 100%;
  }

  .progress {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: .96rem;
    z-index: 2;
    padding: 0 .32rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    pointer-events: auto;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.0001) 0%, rgba(0, 0, 0, 0.4) 100%);

    --van-slider-active-background: rgba(255, 255, 255, 1);
    --van-slider-inactive-background: rgba(255, 255, 255, 0.2);
  }

  --kling-custom-button-width:26px;
  --kling-half-custom-button-width:calc(var(--kling-custom-button-width) / 2);

  .timer {
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    top: -.72rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.48rem;
    line-height: .72rem;
    font-weight: 700;
    color: #fff;
    opacity: 1;
    transition: all 1s ease-out;

    .time-box {
      .duration {
        color: rgba(255, 255, 255, 0.4);
      }
    }
  }

  .custom-button {
    width: .12rem;
    height: .12rem;
    border-radius: 50%;
    background-color: #fff;
    position: relative;
    opacity: 1;
    transition: all 1s ease-out;

    &:active {
      opacity: 1 !important;
    }

    // 扩大热区
    &::before {
      padding: .4rem;
      content: "";
      position: absolute;
      left: 50%;
      top: 50%;
      width: 100%;
      height: 100%;
      transform: translate(-50%, -50%);
    }
  }
}
</style>
